// Pure decision logic for the "get shwifty" button journey (VidamorPage). No DOM,
// no Svelte — positions in, positions out — so it is unit-tested in isolation
// (tests/unit/vidamorDodge.test.ts). The component owns the pointer wiring and
// feeds these functions the measured container/button box.
//
// The journey is a little war with a narrative arc: the button starts cocky
// and taunting, realizes you're serious, goes all-out competitive, then starts
// negotiating, and finally gives up — collapsing to the CENTER of the arena
// (where the video will reveal) and lying there breathing. Clicking the tired
// button starts the FINALE — a victory spin with confetti — and then the video
// plays. While the war is on, the button roams the FULL VIEWPORT (fixed
// positioning, handled by the component), with every jump forced to travel a
// real distance so it never shuffles in place — especially on mobile.
// Keyboard/assistive-tech activation and reduced-motion users skip all of it
// and play on the first click (handled by the caller via these predicates).

export type MoveKind =
  | 'hop'
  | 'spin'
  | 'backflip'
  | 'squeeze'
  | 'dash'
  | 'frantic'
  | 'wobble'
  | 'shake'
  | 'plead'
  | 'stagger'
  | 'droop';

export interface JourneyMove {
  kind: MoveKind;
  /** Button label while this move plays — the arc goes cocky → serious →
   *  negotiating → giving up. */
  label: string;
}

/** One entry per evasion, in order. Each kind is unique so its CSS animation
 *  triggers exactly once when the class is applied. */
export const JOURNEY: readonly JourneyMove[] = [
  // cocky
  { kind: 'hop', label: 'get shwifty' },
  { kind: 'spin', label: '😏 too slow' },
  { kind: 'backflip', label: '🤸 not even close' },
  { kind: 'squeeze', label: '😂 ok this is fun' },
  // ...oh. you're serious.
  { kind: 'dash', label: '😳 wait. you’re serious?' },
  { kind: 'frantic', label: '😤 fine. game on.' },
  // negotiating
  { kind: 'wobble', label: '🥴 ...you’re fast' },
  { kind: 'shake', label: '😰 can we take a break?' },
  { kind: 'plead', label: '🙏 I was JOKING, okay?!' },
  // giving up
  { kind: 'stagger', label: '🥵 ...okay wait' },
  { kind: 'droop', label: '🥱 ...just a little nap' }
] as const;

/** Label on the collapsed, breathing button — the war is over, you won. */
export const TIRED_LABEL = '😮‍💨 okay fine. you won.';

/** How long the finale (victory spin + confetti) runs before the video reveals. */
export const FINALE_MS = 1400;

export interface DodgeBox {
  /** Container inner width in px (the bounded play area). */
  containerWidth: number;
  /** Container inner height in px. */
  containerHeight: number;
  /** Button width in px. */
  buttonWidth: number;
  /** Button height in px. */
  buttonHeight: number;
}

export interface DodgePoint {
  x: number;
  y: number;
}

/**
 * Whether a pointer attempt should trigger the next evasion move. Reduced-motion
 * callers pass reducedMotion=true and the button never evades (first click
 * plays). Once the journey is exhausted the button is tired and stays put.
 */
export function shouldEvade(evasions: number, reducedMotion: boolean): boolean {
  if (reducedMotion) return false;
  return evasions < JOURNEY.length;
}

/**
 * Whether the button has run out of moves and is lying at the bottom breathing.
 * Under reduced motion the button is never tired — it plays on first click
 * without the theatrics (the caller checks reducedMotion before the finale).
 */
export function isTired(evasions: number, reducedMotion: boolean): boolean {
  if (reducedMotion) return false;
  return evasions >= JOURNEY.length;
}

/** The move that plays for evasion number n (1-indexed). Clamped so any
 *  out-of-range n still yields a legal move. */
export function moveForEvasion(evasion: number): JourneyMove {
  const index = Math.min(Math.max(evasion, 1), JOURNEY.length) - 1;
  return JOURNEY[index];
}

/** Clamp a single axis so the button stays fully inside [0, extent]. */
export function clampAxis(value: number, extent: number): number {
  if (extent <= 0) return 0;
  if (value < 0) return 0;
  if (value > extent) return extent;
  return value;
}

/**
 * Clamp a candidate top-left position so the whole button stays inside the
 * container. Never returns a position that overflows the bounded area, so the
 * button can't escape or overlap the surrounding text.
 */
export function clampPosition(candidate: DodgePoint, box: DodgeBox): DodgePoint {
  const maxX = Math.max(0, box.containerWidth - box.buttonWidth);
  const maxY = Math.max(0, box.containerHeight - box.buttonHeight);
  return {
    x: clampAxis(candidate.x, maxX),
    y: clampAxis(candidate.y, maxY)
  };
}

/** Every roam must travel at least this fraction of the play area's diagonal —
 *  the jumps stay properly spaced out instead of shuffling in place. */
export const MIN_ROAM_FRACTION = 0.32;

/**
 * Pick the next roam position inside the play area (the full viewport during
 * the war). `rand` is a 0..1 source (Math.random in production, a stub in
 * tests) sampled twice per candidate. Candidates closer than MIN_ROAM_FRACTION
 * of the diagonal to `current` are resampled (up to 8 tries, then the farthest
 * candidate wins), so consecutive positions are always well spaced. The result
 * is always clamped inside bounds, so any rand in [0,1] yields a legal position.
 */
export function nextRoamPosition(
  box: DodgeBox,
  current: DodgePoint | null,
  rand: () => number
): DodgePoint {
  const maxX = Math.max(0, box.containerWidth - box.buttonWidth);
  const maxY = Math.max(0, box.containerHeight - box.buttonHeight);
  const minDist = MIN_ROAM_FRACTION * Math.hypot(maxX, maxY);
  let best: DodgePoint = { x: 0, y: 0 };
  let bestDist = -1;
  for (let i = 0; i < 8; i++) {
    const candidate = { x: rand() * maxX, y: rand() * maxY };
    const dist = current
      ? Math.hypot(candidate.x - current.x, candidate.y - current.y)
      : Infinity;
    if (dist >= minDist) return clampPosition(candidate, box);
    if (dist > bestDist) {
      bestDist = dist;
      best = candidate;
    }
  }
  return clampPosition(best, box);
}

/** Where the exhausted button collapses to: the CENTER of the arena — the same
 *  spot the video reveals into, so the finale spins in place. */
export function tiredPosition(box: DodgeBox): DodgePoint {
  return clampPosition(
    {
      x: (box.containerWidth - box.buttonWidth) / 2,
      y: (box.containerHeight - box.buttonHeight) / 2
    },
    box
  );
}

/** Vidamor palette pieces for the finale burst. */
export const CONFETTI_COLORS: readonly string[] = [
  '#dd8140',
  '#f0a566',
  '#a64b2e',
  '#d0492b',
  '#fbf0e1'
] as const;

export interface ConfettiPiece {
  /** Horizontal fling distance in px (negative = left). */
  dx: number;
  /** Vertical fling distance in px (negative = up; upward-biased). */
  dy: number;
  /** End rotation in degrees. */
  rot: number;
  /** Size multiplier, 0.6–1.4. */
  scale: number;
  color: string;
  /** Stagger in ms so the burst reads as a crackle, not a single frame. */
  delay: number;
}

/**
 * Generate the finale burst. `rand` is a 0..1 source (Math.random in production,
 * a stub in tests). Distances are bounded so pieces stay near the arena.
 */
export function confettiPieces(count: number, rand: () => number): ConfettiPiece[] {
  const pieces: ConfettiPiece[] = [];
  for (let i = 0; i < count; i++) {
    pieces.push({
      dx: (rand() * 2 - 1) * 220,
      dy: -(rand() * 200 + 20) + rand() * 80,
      rot: (rand() * 2 - 1) * 720,
      scale: 0.6 + rand() * 0.8,
      color: CONFETTI_COLORS[Math.floor(rand() * CONFETTI_COLORS.length) % CONFETTI_COLORS.length],
      delay: Math.floor(rand() * 220)
    });
  }
  return pieces;
}
